// piezo-anomaly-status.component.ts
import { Component, inject, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { trigger, transition, style, animate } from "@angular/animations";
import { MatIcon } from "@angular/material/icon";
import { HttpClient } from "@angular/common/http";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { PiezeoDashboardService } from "../../../../../app/services/piezo-dashboard.service";
import { BackendApiService } from "../../../../../app/services/backend-api.service";
import moment from "moment-timezone";
import { ConvertDatetimePipe } from "../../../../../app/convert-datetime.pipe";
import { BaseUrlConstantsService } from "../../../../../app/services/base-url-constants.service";

interface PiezoAnomalyData {
  timeUpdatedAt: string;
  overall_status: string;
  action_required: string;
}

@Component({
  selector: "app-piezo-anomaly-card",
  templateUrl: "./piezo-anomaly-status.component.html",
  styleUrls: ["./piezo-anomaly-status.component.scss"],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIcon, MatProgressSpinnerModule],
  animations: [
    trigger("fadeInOut", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("300ms", style({ opacity: 1 })),
      ]),
      transition(":leave", [animate("300ms", style({ opacity: 0 }))]),
    ]),
    trigger("slideInOut", [
      transition(":enter", [
        style({ transform: "translateY(50px)", opacity: 0 }),
        animate(
          "400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          style({ transform: "translateY(0)", opacity: 1 })
        ),
      ]),
      transition(":leave", [
        animate(
          "300ms ease-in",
          style({ transform: "translateY(50px)", opacity: 0 })
        ),
      ]),
    ]),
    trigger("bounceIn", [
      transition(":enter", [
        style({ transform: "scale(0.3)", opacity: 0 }),
        animate(
          "600ms cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          style({ transform: "scale(1)", opacity: 1 })
        ),
      ]),
    ]),
  ],
})
export class PiezoAnomalyCard implements OnInit {
  anomaly_status: PiezoAnomalyData = {
    timeUpdatedAt: "",
    overall_status: "",
    action_required: "",
  };

  anomaly_status_loader: boolean = false;
  isReportModalOpen: boolean = false;
  isLoading: boolean = false;
  today = new Date();
  reportSize = "2.4 MB";
  currentStep = 1;
  progressPercentage = 0;
  processingStepText = "Collecting Data";
  SAONDEMANDRESPONSE: any;
  isDownloading: boolean = false;
  public token: any = localStorage.getItem("token") || "not available";
  private processingTimers: any[] = [];
  statusClass: string = "idle";
  client_id = null;

  srv = inject(PiezeoDashboardService);
  http = inject(BackendApiService);

  piezo_asset_ids: any[] = [];
  emsaver_asset_ids: any[] = [];
  subsystem_id = null;
  uuid = null;
  overall_live_status = "1";
  sub_name = "";

  powertrain_generate_report_url = BaseUrlConstantsService.ASSET_REPORT_PDF;
  report_preview_generate = BaseUrlConstantsService.Generate_new_url;
  report_generate = BaseUrlConstantsService.ASSET_REPORT_PDF;
  assetServiceUrl = BaseUrlConstantsService.BASE_ASSET_SERVICE;

  constructor() {}

  ngOnInit(): void {
    let subsystem_data = null;
    this.srv.subsystem_data$.subscribe((data) => {
      subsystem_data = data;
    });
    console.log(subsystem_data);
    this.subsystem_id = subsystem_data.id;
    this.sub_name = subsystem_data.subsystem_name;
    let sensors = JSON.parse(subsystem_data.device_position);
    this.piezo_asset_ids = sensors
      .filter((s) => s.master_type == 4)
      .map((s) => s.asset_id)
      .join(",");
    this.emsaver_asset_ids = sensors
      .filter((s) => s.master_type != 4)
      .map((s) => s.asset_id)
      .join(",");
    this.client_id = subsystem_data.client_id;
    this.fetchPiezoAnomalyStatus();
  }

  fetchPiezoAnomalyStatus() {
    this.anomaly_status_loader = true;
    let base_url=`${BaseUrlConstantsService.BASE_PIEZO_SERVICE}/api/anomaly-status/${this.client_id}/${this.piezo_asset_ids}`;
    // let base_url=`http://localhost:3017/api/anomaly-status/${this.client_id}/${this.piezo_asset_ids}`;
    if(this.emsaver_asset_ids.length>0){
      base_url+=`/${this.emsaver_asset_ids}`;
    }else{
      base_url+='/NULL';
    }
    this.http
      .getDataV2(base_url)
      .then((res: any) => {
        if (res.status != "success") throw new Error();
        this.anomaly_status = res.data;
        let testDateUtc = moment.utc(this.anomaly_status.timeUpdatedAt);
        let localDate = testDateUtc.local();
        let timeUpdatedAt = new ConvertDatetimePipe().convertDateTime(
          localDate
        );
        this.anomaly_status.timeUpdatedAt =
          timeUpdatedAt.split(" ")[0] + " " + timeUpdatedAt.split(" ")[1];
        this.getStatusClass();
        this.anomaly_status_loader = false;
      })
      .catch((err) => {
        console.error(err);
        this.anomaly_status = {
          timeUpdatedAt: "N/A",
          overall_status: "N/A",
          action_required: "N/A",
        };
        this.anomaly_status_loader = false;
      });
  }

  openReportModal() {
    this.isReportModalOpen = true;
    this.resetProcessing();
    this.startReportGeneration();
  }

  closeReportModal() {
    if (!this.isLoading) {
      this.isReportModalOpen = false;
      this.clearTimers();
    }
  }

  startReportGeneration() {
    this.currentStep = 1;
    this.progressPercentage = 33;
    this.processingStepText = "Collecting Data";

    this.processingTimers.push(
      setTimeout(() => {
        this.currentStep = 2;
        this.progressPercentage = 66;
        this.processingStepText = "Performing Report Generation";
        this.callGenerateReportApi();
      }, 1000)
    );
  }

  callGenerateReportApi() {
    const body = {
      client_id: this.client_id,
      subsystem_id: this.subsystem_id,
    };

    const headers = {
      "Content-Type": "application/json",
      Authorization: `${this.token}`,
      token: this.token,
      Timezone: localStorage.getItem("clientTimeZone") || "not available",
    };

    // TODO: Replace with your actual piezo report generation endpoint
    const reportUrl = `${this.report_generate}generate_piezo_powertrain_report`;
    console.log(reportUrl)
    this.http
      .postData(reportUrl, body)
      .then((response) => {
        console.log("Piezo Report API Response:", response);

        this.SAONDEMANDRESPONSE = response;

        this.processingTimers.push(
          setTimeout(() => {
            this.currentStep = 3;
            this.progressPercentage = 100;
            this.processingStepText = "Report Generated";
            this.isLoading = false;
            this.today = new Date();
          }, 500)
        );
      })
      .catch((err) => {
        console.error("Error calling Piezo Report API:", err);
        this.clearTimers();
        this.isLoading = false;
        alert("Failed to generate report. Please try again.");
      });
  }

  resetProcessing() {
    this.clearTimers();
    this.currentStep = 1;
    this.progressPercentage = 33;
    this.processingStepText = "Collecting Data";
    this.isLoading = true;
  }

  clearTimers() {
    this.processingTimers.forEach((timer) => clearTimeout(timer));
    this.processingTimers = [];
  }

  downloadReport() {
    this.isDownloading = true;
    console.log("Downloading piezo report...");

    let url = this.SAONDEMANDRESPONSE?.data?.assetBody?.report_url;
    console.log("Report URL:", url);

    // TODO: Implement your download logic here
    let body: any = {
      key: url,
    };

    // Simulating download for now
    setTimeout(() => {
      this.isDownloading = false;
      console.log("Download complete");
    }, 2000);

    // Uncomment when ready:
    this.http
      .postData(`${this.assetServiceUrl}/download-report`, body)
      .then((res:any) => {
        const responseData = res?.data;

        if (responseData?.status === "success" && responseData?.pdfFilePath) {
          const link = document.createElement("a");
          link.href = responseData.pdfFilePath;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        this.isDownloading = false;
      })
      .catch((err) => {
        console.error("Download error:", err);
        this.isDownloading = false;
      });
  }

  previewReport() {
    console.log("Previewing piezo report...");
    let url = this.SAONDEMANDRESPONSE?.data?.assetBody?.report_url;

    if (!url) {
      alert("Report URL not available");
      return;
    }

    // TODO: Implement preview logic
    let body: any = {
      key: url,
    };

    this.http
      .postData(`${this.report_preview_generate}/pre-signed-url`, body)
      .then((res: any) => {
        if (res?.status === "success" && res?.body) {
          window.open(res.body, "_blank");
        }
      })
      .catch((err) => {
        console.error("Preview error:", err);
      });
  }

  getStatusClass() {
    // if (this.overall_live_status == "2") {
    //   this.statusClass = "idle";
    //   return;
    // }
    switch (this.anomaly_status?.overall_status) {
      case "UNHEALTHY":
        this.statusClass = "anomaly_orange";
        break;
      case "ERROR":
        this.statusClass = "anomaly_red";
        break;
      case "HEALTHY":
        this.statusClass = "anomaly_green";
        break;
      default:
        this.statusClass = "idle";
    }
  }
}
